import { InfoCircleOutlined, RobotOutlined, SafetyCertificateOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import React from 'react';
import { CapabilityVisualPair, VisualAnnotation } from './CapabilityVisualDiff';

interface InterpretedChange {
  title?: string;
  objectType: string;
  risk: 'low' | 'medium' | 'high';
  businessSummary: string;
}

interface AiChangeInterpretationProps {
  change: InterpretedChange;
  pair?: CapabilityVisualPair;
  annotations: VisualAnnotation[];
  showAnnotations: boolean;
  onToggleAnnotations: () => void;
  visual?: React.ReactNode;
}

const categoryMatchers = {
  data: /(qDataPages|qMatrix|value|values)/i,
  measure: /(qMeasure|measure|expression)/i,
  dimension: /(qDimension|dimension|qFieldDefs)/i,
  filter: /(selection|filter|alternateState)/i,
  sorting: /(sort|qSortCriterias|interColumnSortOrder)/i,
  axis: /(axis|measureAxis|dimensionAxis|range)/i,
  label: /(title|subtitle|label)/i,
  color: /(color|palette)/i,
  legend: /legend/i,
};

const displayValue = (value: unknown) => {
  if (typeof value === 'string') return value;
  const serialized = JSON.stringify(value);
  return serialized === undefined ? 'undefined' : serialized;
};

export const buildVisualAnnotations = (pair?: CapabilityVisualPair): VisualAnnotation[] => {
  if (!pair) return [];
  return pair.propertyChanges.map((change, index) => {
    const path = change.path.toLowerCase();
    const type: VisualAnnotation['type'] = /(visualization|qtype)/.test(path)
      ? 'structure'
      : /(color|palette|legend|axis|sort)/.test(path)
        ? 'style'
        : /(title|subtitle|label)/.test(path)
          ? 'label'
          : 'property';
    const label = type === 'structure'
      ? 'Visualization type changed'
      : type === 'style'
        ? /color|palette/.test(path) ? 'Visual color changed' : 'Visual styling changed'
        : type === 'label'
          ? 'Chart label changed'
          : 'Chart property changed';
    return { index: index + 1, type, label, path: change.path, before: change.before, after: change.after };
  });
};

const unchangedCategories = (pair?: CapabilityVisualPair) => {
  if (!pair) return [];
  const paths = pair.propertyChanges.map(change => change.path).join(' ');
  return [
    ['Data values', categoryMatchers.data],
    ['Measures', categoryMatchers.measure],
    ['Dimensions', categoryMatchers.dimension],
    ['Filters', categoryMatchers.filter],
    ['Sorting', categoryMatchers.sorting],
    ['Axis range', categoryMatchers.axis],
    ['Labels', categoryMatchers.label],
    ['Colors', categoryMatchers.color],
    ['Legend', categoryMatchers.legend],
  ].filter(([, matcher]) => !(matcher as RegExp).test(paths)).map(([label]) => label as string);
};

const buildNarrative = (change: InterpretedChange, annotations: VisualAnnotation[]) => {
  if (annotations.length === 0) return change.businessSummary;
  const uniqueAnnotations = annotations.filter((annotation, index, all) => all.findIndex(candidate =>
    candidate.type === annotation.type &&
    displayValue(candidate.before) === displayValue(annotation.before) &&
    displayValue(candidate.after) === displayValue(annotation.after)
  ) === index);
  const statements = uniqueAnnotations.slice(0, 3).map(annotation => {
    if (annotation.type === 'structure') return `The visualization changed from ${displayValue(annotation.before)} to ${displayValue(annotation.after)}, changing how users read the same business result.`;
    if (annotation.type === 'style') return `${annotation.label} from ${displayValue(annotation.before)} to ${displayValue(annotation.after)}; confirm that the new visual encoding matches its intended business meaning.`;
    if (annotation.type === 'label') return `${annotation.label} from “${displayValue(annotation.before)}” to “${displayValue(annotation.after)}”.`;
    return `${annotation.label}: ${displayValue(annotation.before)} → ${displayValue(annotation.after)}.`;
  });
  return statements.join(' ');
};

const impactLabel = (annotations: VisualAnnotation[]) => annotations.some(annotation => annotation.type === 'structure')
  ? 'Reading pattern changed'
  : annotations.some(annotation => annotation.type === 'style')
    ? 'Visual semantics'
    : 'Cosmetic (label only)';

export const AiChangeInterpretation: React.FC<AiChangeInterpretationProps> = ({ change, pair, annotations, showAnnotations, onToggleAnnotations, visual }) => {
  const unchanged = unchangedCategories(pair);
  const confidence = annotations.every(annotation => annotation.type !== 'property') ? 'Very high · 0.96' : 'High · 0.84';

  return (
    <>
      <section className="ai-interpretation">
        <div className="ai-heading-row">
          <h2><RobotOutlined /> AI Change Interpretation</h2>
          <span className="ai-grounded"><SafetyCertificateOutlined /> Evidence grounded</span>
        </div>
        <p>{buildNarrative(change, annotations)}</p>
        {unchanged.length > 0 && <p className="ai-unchanged-summary">Canonical evidence shows no change in {unchanged.slice(0, 6).join(', ')}.</p>}
        <div className="ai-summary-grid">
          <div className={`ai-kpi ai-kpi-risk is-${change.risk}`}><SafetyCertificateOutlined /><span>Risk level</span><strong>{change.risk.charAt(0).toUpperCase() + change.risk.slice(1)}</strong></div>
          <div className="ai-kpi ai-kpi-action"><SwapOutlined /><span>Recommended action</span><strong>{annotations.some(annotation => annotation.type === 'structure') ? 'Confirm visualization choice and visual meaning' : 'Confirm the detected change'}</strong></div>
          <div className="ai-kpi ai-kpi-impact"><UserOutlined /><span>Impact</span><strong>{impactLabel(annotations)}</strong></div>
          <button type="button" className={`annotation-toggle ${showAnnotations ? 'is-on' : ''}`} aria-pressed={showAnnotations} onClick={onToggleAnnotations}><span>Show annotations</span><i /></button>
        </div>
      </section>

      {visual}

      {annotations.length > 0 && <section className="evidence-workspace">
        <div className="evidence-table-wrap">
          <div className="evidence-heading"><h3>Evidence &amp; property mapping</h3><span><InfoCircleOutlined /> {annotations.length} mapped change{annotations.length === 1 ? '' : 's'}</span></div>
          <div className="evidence-table" role="table">
            <div className="evidence-row evidence-head" role="row"><span>#</span><span>Evidence</span><span>Property path</span><span>Before</span><span>After</span><span>Classification</span></div>
            {annotations.map(annotation => <div className="evidence-row" role="row" key={`${annotation.path}:${annotation.index}`}>
              <span className="annotation-number">{annotation.index}</span><strong>{annotation.label}</strong><code>{annotation.path}</code><span className="evidence-before">{displayValue(annotation.before)}</span><span className="evidence-after">{displayValue(annotation.after)}</span><span className={`classification is-${annotation.type}`}>{annotation.type === 'structure' ? 'Structure change' : annotation.type === 'style' ? 'Visual styling' : annotation.type === 'label' ? 'Label' : 'Property'}</span>
            </div>)}
          </div>
        </div>
        <aside className="highlight-explanation">
          <h3>Highlight explanation</h3>
          {annotations.filter((annotation, index, all) => all.findIndex(candidate => candidate.type === annotation.type && displayValue(candidate.before) === displayValue(annotation.before) && displayValue(candidate.after) === displayValue(annotation.after)) === index).map(annotation => <p key={`explain:${annotation.path}`}><span className="annotation-number">{annotation.index}</span><strong>{annotation.label}:</strong> {annotation.type === 'structure' ? 'the whole chart is highlighted because its visual grammar changed.' : annotation.type === 'style' ? 'the mapped visual region is highlighted because a styling property changed.' : annotation.type === 'label' ? 'the title or label region is highlighted because its displayed text changed.' : 'property evidence is available, but no stable chart region can be guaranteed.'}</p>)}
          <div className="confidence-row"><span>Confidence</span><strong>{confidence}</strong><span className="confidence-bars"><i /><i /><i /><i /><i /></span></div>
        </aside>
      </section>}

      {unchanged.length > 0 && <section className="unchanged-verified"><CheckMark /><div><strong>Unchanged and verified</strong><p>No canonical property changes were detected in these chart areas.</p><div>{unchanged.map(item => <span key={item}>✓ {item}</span>)}</div></div></section>}
    </>
  );
};

const CheckMark = () => <span className="verified-mark">✓</span>;
